import OpenAI, { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Analyzes estimate data and provides cost-saving recommendations.
 * @param {Object} estimateData - Complete estimate data including calculations, engineers, materials, licenses, config.
 * @returns {Promise<Object>} Analysis results with recommendations.
 */
export async function analyzeEstimate(estimateData) {
  try {
    const { deliveryMode, selectedEngineers, selectedLicenses, selectedMaterials, config, calculations } = estimateData;

    const prompt = `You are an expert cost analyst for service-based projects. Analyze the following project estimate and provide actionable cost-saving recommendations.

**Project Details:**
- Delivery Mode: ${deliveryMode}
- Total Engineers: ${selectedEngineers?.length} roles (${selectedEngineers?.reduce((sum, eng) => sum + eng?.count, 0)} people)
- Total Materials: ${selectedMaterials?.length} items
- Total Licenses: ${selectedLicenses?.length} licenses
- Overhead: ${config?.overhead}%
- Target Margin: ${config?.targetMargin}%
- Contingency: ${config?.contingency}%

**Cost Breakdown:**
- Labor Costs: OMR ${calculations?.totalEngineerCosts?.toFixed(3)}
- Materials Cost: OMR ${calculations?.totalMaterialsCost?.toFixed(3)}
- Licenses Cost: OMR ${calculations?.totalLicensesCosts?.toFixed(3)}
- Direct Costs: OMR ${calculations?.directCosts?.toFixed(3)}
- Overhead Costs: OMR ${calculations?.overheadCosts?.toFixed(3)}
- Contingency Costs: OMR ${calculations?.contingencyCosts?.toFixed(3)}
- Total Cost: OMR ${calculations?.totalCost?.toFixed(3)}
- Sell Price: OMR ${calculations?.sellPrice?.toFixed(3)}
- Profit Margin: ${calculations?.profitMarginPercentage?.toFixed(2)}%

**Engineer Details:**
${selectedEngineers?.map(eng => `- ${eng?.role}: ${eng?.count} person(s), ${eng?.hours} hours, OMR ${eng?.costPerHour}/hr`)?.join('\n')}

**Materials Details:**
${selectedMaterials?.map(mat => `- ${mat?.name}: ${mat?.quantity} units @ $${mat?.unitCost}, ${mat?.wastePercentage}% waste`)?.join('\n')}

**Licenses Details:**
${selectedLicenses?.map(lic => `- ${lic?.name}: ${lic?.quantity} licenses, ${lic?.type === 'perpetual' ? `$${lic?.oneTimeCost} one-time` : `$${lic?.monthlyCost}/mo for ${lic?.duration} months`}`)?.join('\n')}

Provide your analysis in the following structured format:
1. Cost-saving strategies (3-5 specific, actionable recommendations)
2. Alternative delivery mode suggestions (compare current vs alternatives with estimated savings)
3. Resource optimization recommendations (engineer allocation, material waste reduction, license optimization)
4. Overall risk assessment and confidence level`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert cost analyst specializing in service project estimation. Provide specific, actionable, and data-driven recommendations. Be concise but thorough.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'estimate_analysis',
          schema: {
            type: 'object',
            properties: {
              costSavingStrategies: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    title: { type: 'string' },
                    description: { type: 'string' },
                    estimatedSavings: { type: 'string' },
                    priority: { type: 'string', enum: ['high', 'medium', 'low'] }
                  },
                  required: ['title', 'description', 'estimatedSavings', 'priority']
                }
              },
              deliveryModeAlternatives: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    mode: { type: 'string' },
                    rationale: { type: 'string' },
                    estimatedSavings: { type: 'string' },
                    tradeoffs: { type: 'string' }
                  },
                  required: ['mode', 'rationale', 'estimatedSavings', 'tradeoffs']
                }
              },
              resourceOptimization: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    category: { type: 'string', enum: ['engineers', 'materials', 'licenses', 'configuration'] },
                    recommendation: { type: 'string' },
                    impact: { type: 'string' }
                  },
                  required: ['category', 'recommendation', 'impact']
                }
              },
              overallAssessment: {
                type: 'object',
                properties: {
                  summary: { type: 'string' },
                  totalPotentialSavings: { type: 'string' },
                  riskLevel: { type: 'string', enum: ['low', 'medium', 'high'] },
                  confidence: { type: 'string', enum: ['low', 'medium', 'high'] }
                },
                required: ['summary', 'totalPotentialSavings', 'riskLevel', 'confidence']
              }
            },
            required: ['costSavingStrategies', 'deliveryModeAlternatives', 'resourceOptimization', 'overallAssessment'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'medium'
    });

    const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
    return {
      success: true,
      data: analysis,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error analyzing estimate:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Fetches current market prices and best practices for master data items.
 * @param {string} itemQuery - Description of the item to search for.
 * @param {string} dataType - Type of data (materials, engineers, licenses, vendors, etc.).
 * @returns {Promise<Object>} Price analysis with suggestions and best practices.
 */
export async function fetchPricesAndBestPractices(itemQuery, dataType) {
  try {
    const dataTypePrompts = {
      'materials': 'construction materials, fiber optic cables, network equipment, and hardware components',
      'engineers': 'engineering roles, technical specialists, and professional services rates',
      'software': 'software licenses, SaaS subscriptions, and enterprise tools',
      'vendors': 'vendor information, supplier details, and service providers',
      'fixed-expenses': 'fixed operational expenses, recurring costs, and overhead items',
      'variable-expenses': 'variable costs, hourly rates, and per-unit expenses'
    };

    const contextDescription = dataTypePrompts?.[dataType] || 'general items';

    const prompt = `You are an expert procurement analyst with access to current market data. Analyze the following query and provide current market prices, price ranges, and best practices.

**Query:** "${itemQuery}"
**Category:** ${contextDescription}

Provide comprehensive market analysis including:
1. Current market price ranges and typical costs
2. Multiple item suggestions with realistic pricing
3. Best practices for procurement and cost optimization
4. Market insights and trends
5. Quality considerations and vendor recommendations

Be specific with numbers and provide actionable recommendations based on current market conditions.`;

    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert procurement and pricing analyst with deep knowledge of market rates, industry standards, and best practices. Provide accurate, current market data with specific price ranges and actionable recommendations.' 
        },
        { role: 'user', content: prompt },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'price_analysis',
          schema: {
            type: 'object',
            properties: {
              marketInsights: {
                type: 'string',
                description: 'Overview of current market conditions and pricing trends'
              },
              suggestedItems: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    name: { type: 'string' },
                    itemName: { type: 'string' },
                    itemCode: { type: 'string' },
                    description: { type: 'string' },
                    category: { type: 'string' },
                    unitCost: { type: 'number' },
                    costPerDay: { type: 'number' },
                    cost: { type: 'number' },
                    monthlyCost: { type: 'number' },
                    priceRange: { type: 'string' },
                    uom: { type: 'string' },
                    wastePercentage: { type: 'number' },
                    role: { type: 'string' },
                    specialization: { type: 'string' },
                    internalCost: { type: 'number' },
                    billableRate: { type: 'number' },
                    vendor: { type: 'string' },
                    licenseType: { type: 'string' },
                    numberOfLicenses: { type: 'number' }
                  },
                  required: ['name', 'description']
                },
                description: 'List of suggested items with pricing'
              },
              bestPractices: {
                type: 'array',
                items: { type: 'string' },
                description: 'Best practices for procurement and cost optimization'
              },
              confidence: {
                type: 'string',
                enum: ['high', 'medium', 'low'],
                description: 'Confidence level in the pricing data'
              }
            },
            required: ['marketInsights', 'suggestedItems', 'bestPractices', 'confidence'],
            additionalProperties: false
          }
        }
      },
      reasoning_effort: 'high',
      verbosity: 'high'
    });

    const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
    return {
      success: true,
      data: analysis,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error fetching prices:', error);
    }
    throw new Error(errorInfo.message);
  }
}

export default { analyzeEstimate, fetchPricesAndBestPractices };